/**
 * scene3d.js – Three.js 3D mode
 * Drives every key in SAT_CONFIG generically.
 * Constellations → Points clouds.  Singles → sphere+ring+nadir.
 */

import * as THREE from "three";
import { propagateNow, groundTrackPoints } from "./propagate.js";
import { SAT_CONFIG } from "./satellites.js";

const EARTH_R = 1;
const KM_TO_R = EARTH_R / 6371;

// ── Module state ──────────────────────────────────────────
let renderer, camera, scene, animId;
let earthGroup;
let earth, graticule, atmosphere, starsMesh;
let continentLines = null;
let resizeObserver = null;

const markers     = {};   // key → THREE.Group  (singles only)
const orbitMeshes = {};   // key → THREE.Line   (singles only)
const nadir       = {};   // key → {dot,line}   (singles only)
const constClouds = {};   // key → THREE.Points (constellations)

let rotX = 0, rotY = 0;
let isDragging = false, prevMouse = { x: 0, y: 0 };
let autoRotate = true;
let showTracks = true;

// ── Helpers ───────────────────────────────────────────────
function llh2v3(lat, lon, altKm) {
  const r     = EARTH_R + altKm * KM_TO_R;
  const phi   = THREE.MathUtils.degToRad(lat);
  const theta = THREE.MathUtils.degToRad(lon);
  return new THREE.Vector3(
    r * Math.cos(phi) * Math.sin(theta),
    r * Math.sin(phi),
    r * Math.cos(phi) * Math.cos(theta)
  );
}

function makeEarthMat() {
  return new THREE.MeshPhongMaterial({
    color: 0x0d2137, emissive: 0x060f1a, specular: 0x1a3a5c, shininess: 40,
  });
}

// ── Graticule ─────────────────────────────────────────────
function buildGraticule() {
  const mat = new THREE.LineBasicMaterial({ color: 0x1a3a5c, transparent: true, opacity: 0.3 });
  const grp = new THREE.Group();
  const R   = EARTH_R + 0.001;
  for (let lat = -75; lat <= 75; lat += 15) {
    const pts = [], phi = THREE.MathUtils.degToRad(lat);
    for (let i = 0; i <= 64; i++) {
      const t = (2 * Math.PI * i) / 64;
      pts.push(new THREE.Vector3(R*Math.cos(phi)*Math.sin(t), R*Math.sin(phi), R*Math.cos(phi)*Math.cos(t)));
    }
    grp.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), mat));
  }
  for (let lon = 0; lon < 360; lon += 15) {
    const pts = [], theta = THREE.MathUtils.degToRad(lon);
    for (let i = 0; i <= 64; i++) {
      const phi = Math.PI*(i/64) - Math.PI/2;
      pts.push(new THREE.Vector3(R*Math.cos(phi)*Math.sin(theta), R*Math.sin(phi), R*Math.cos(phi)*Math.cos(theta)));
    }
    grp.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), mat));
  }
  return grp;
}

// ── Stars ─────────────────────────────────────────────────
function buildStars() {
  const verts = [];
  for (let i = 0; i < 5000; i++) {
    const t = Math.random()*2*Math.PI, p = Math.acos(2*Math.random()-1), r = 80+Math.random()*20;
    verts.push(r*Math.sin(p)*Math.cos(t), r*Math.sin(p)*Math.sin(t), r*Math.cos(p));
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
  return new THREE.Points(geo, new THREE.PointsMaterial({ color: 0xffffff, size: 0.12, sizeAttenuation: true }));
}

// ── Continent outlines ────────────────────────────────────
async function buildContinentLines() {
  if (!scene) return;
  const R   = EARTH_R + 0.003;
  const URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/land-110m.json";
  let topo;
  try {
    const res = await fetch(URL);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    topo = await res.json();
  } catch (e) { console.warn("Continent data failed:", e); return; }

  const { arcs, transform: { scale, translate } } = topo;
  const decodedArcs = arcs.map((arc) => {
    let x = 0, y = 0;
    return arc.map(([dx,dy]) => { x+=dx; y+=dy; return [x*scale[0]+translate[0], y*scale[1]+translate[1]]; });
  });
  const resolveArc = (idx) => idx >= 0 ? decodedArcs[idx] : decodedArcs[~idx].slice().reverse();
  const toV3 = ([lon, lat]) => {
    const phi = THREE.MathUtils.degToRad(lat), theta = THREE.MathUtils.degToRad(lon);
    return new THREE.Vector3(R*Math.cos(phi)*Math.sin(theta), R*Math.sin(phi), R*Math.cos(phi)*Math.cos(theta));
  };
  const allPts = [];
  function processGeom(geom) {
    if (!geom) return;
    if (geom.type === "Polygon") {
      geom.arcs.forEach((ring) => {
        const verts = ring.flatMap(resolveArc).map(toV3);
        for (let i = 0; i < verts.length-1; i++) allPts.push(verts[i], verts[i+1]);
      });
    } else if (geom.type === "MultiPolygon") {
      geom.arcs.forEach((poly) => poly.forEach((ring) => {
        const verts = ring.flatMap(resolveArc).map(toV3);
        for (let i = 0; i < verts.length-1; i++) allPts.push(verts[i], verts[i+1]);
      }));
    } else if (geom.type === "GeometryCollection") {
      geom.geometries.forEach(processGeom);
    }
  }
  processGeom(topo.objects.land);
  if (!allPts.length || !scene) return;
  const geo = new THREE.BufferGeometry().setFromPoints(allPts);
  const mat = new THREE.LineBasicMaterial({ color: 0x2a6e3f, transparent: true, opacity: 0.85 });
  continentLines = new THREE.LineSegments(geo, mat);
  earthGroup.add(continentLines);
}

// ── Single-sat marker (sphere + ring) ────────────────────
function buildSingleMarker(key) {
  const color = SAT_CONFIG[key]?.color || 0xffffff;
  const grp   = new THREE.Group();
  grp.add(
    new THREE.Mesh(new THREE.SphereGeometry(0.025,12,12),
      new THREE.MeshBasicMaterial({ color })),
    new THREE.Mesh(new THREE.RingGeometry(0.032,0.052,32),
      new THREE.MeshBasicMaterial({ color, transparent:true, opacity:0.35, side:THREE.DoubleSide }))
  );
  earthGroup.add(grp);
  markers[key] = grp;
}

// ── Nadir line ────────────────────────────────────────────
function buildNadir(key) {
  const color = SAT_CONFIG[key]?.color || 0xffffff;
  const dot   = new THREE.Mesh(new THREE.SphereGeometry(0.012,8,8),
    new THREE.MeshBasicMaterial({ color, transparent:true, opacity:0.5 }));
  const line  = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]),
    new THREE.LineBasicMaterial({ color, transparent:true, opacity:0.25 })
  );
  earthGroup.add(dot, line);
  nadir[key] = { dot, line };
}

// ── Constellation point cloud ─────────────────────────────
export function updateConstellationCloud3D(key, tleList) {
  if (!scene || !earthGroup) return;          // scene not ready yet
  const color = SAT_CONFIG[key]?.color || 0x4ade80;

  const positions = [];
  tleList.forEach((tle) => {
    const pos = propagateNow(tle.tle1, tle.tle2);
    if (!pos) return;
    const v = llh2v3(pos.lat, pos.lon, pos.alt);
    positions.push(v.x, v.y, v.z);
  });
  if (!positions.length) return;

  if (constClouds[key]) {
    // Update in-place — but only if it still belongs to the current earthGroup
    if (!earthGroup.children.includes(constClouds[key])) {
      // Stale reference from a previous scene — recreate
      constClouds[key].geometry.dispose();
      constClouds[key].material.dispose();
      delete constClouds[key];
    }
  }

  if (constClouds[key]) {
    const geo = constClouds[key].geometry;
    geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geo.attributes.position.needsUpdate = true;
    geo.setDrawRange(0, positions.length / 3);
  } else {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      color,
      size:            0.04,   // larger — clearly visible at default zoom
      sizeAttenuation: true,
      transparent:     true,
      opacity:         1.0,
    });
    const cloud = new THREE.Points(geo, mat);
    earthGroup.add(cloud);          // attached to current earthGroup ✓
    constClouds[key] = cloud;
    console.debug(`[3D] constellation cloud "${key}" created: ${positions.length/3} points, colour #${color.toString(16).padStart(6,"0")}`);
  }
}

// ── Public: init ──────────────────────────────────────────
export function initScene3D(canvasId) {
  const canvas = document.getElementById(canvasId);
  const wrap   = canvas.parentElement;

  renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 1);
  renderer.setSize(wrap.clientWidth, wrap.clientHeight);

  scene  = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(45, wrap.clientWidth/wrap.clientHeight, 0.1, 1000);
  camera.position.z = 3.5;

  starsMesh = buildStars();
  scene.add(starsMesh);

  earthGroup = new THREE.Group();
  scene.add(earthGroup);

  earth = new THREE.Mesh(new THREE.SphereGeometry(EARTH_R,64,64), makeEarthMat());
  earthGroup.add(earth);

  graticule = buildGraticule();
  earthGroup.add(graticule);

  buildContinentLines();

  atmosphere = new THREE.Mesh(
    new THREE.SphereGeometry(EARTH_R*1.04,32,32),
    new THREE.MeshPhongMaterial({ color:0x4488ff, transparent:true, opacity:0.12, side:THREE.FrontSide, depthWrite:false })
  );
  earthGroup.add(atmosphere);

  scene.add(new THREE.AmbientLight(0x223344, 0.8));
  const sun = new THREE.DirectionalLight(0xfff8ee, 1.4);
  sun.position.set(5, 3, 5);
  scene.add(sun);

  // Only single-type sats get dedicated sphere+nadir
  Object.keys(SAT_CONFIG).forEach((key) => {
    if (SAT_CONFIG[key].type === "single") {
      buildSingleMarker(key);
      buildNadir(key);
    }
  });

  setupInteraction(canvas, wrap);

  resizeObserver = new ResizeObserver(() => {
    if (!renderer) return;
    renderer.setSize(wrap.clientWidth, wrap.clientHeight);
    camera.aspect = wrap.clientWidth / wrap.clientHeight;
    camera.updateProjectionMatrix();
  });
  resizeObserver.observe(wrap);

  renderLoop();
}

// ── Public: destroy ───────────────────────────────────────
export function destroyScene3D() {
  if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null; }
  if (animId) { cancelAnimationFrame(animId); animId = null; }
  if (renderer) { renderer.dispose(); renderer = null; }
  // Null every module-level Three.js reference — earthGroup included.
  // If earthGroup is NOT nulled here, the next initScene3D() creates a
  // fresh group in the new scene but updateConstellationCloud3D() still
  // appends to the stale group (no longer part of any scene → invisible).
  scene = camera = earth = graticule = atmosphere = starsMesh =
    continentLines = earthGroup = null;
  [markers, orbitMeshes, nadir, constClouds].forEach((obj) =>
    Object.keys(obj).forEach((k) => delete obj[k])
  );
  rotX = rotY = 0;
}

// ── Render loop ───────────────────────────────────────────
function renderLoop() {
  animId = requestAnimationFrame(renderLoop);
  if (autoRotate && !isDragging) rotY += 0.001;

  if (earthGroup) {
    earthGroup.rotation.x = rotX;
    earthGroup.rotation.y = rotY;
  }

  Object.values(orbitMeshes).forEach((m) => {
    if (m) { m.rotation.x = rotX; m.rotation.y = rotY; }
  });

  if (earthGroup) {
    const localCam = earthGroup.worldToLocal(camera.position.clone());
    Object.values(markers).forEach((g) => {
      if (g?.children[1]) g.children[1].lookAt(localCam);
    });
  }

  if (renderer && scene && camera) renderer.render(scene, camera);
}

// ── Public: update single satellite ──────────────────────
export function updateSat3D(key, tleEntry) {
  if (!scene) return null;
  const pos = propagateNow(tleEntry.tle1, tleEntry.tle2);
  if (!pos) return null;
  const { lat, lon, alt, velocity } = pos;
  const satPos    = llh2v3(lat, lon, alt);
  const groundPos = llh2v3(lat, lon, 0);
  if (markers[key]) markers[key].position.copy(satPos);
  if (nadir[key]) {
    nadir[key].dot.position.copy(groundPos);
    nadir[key].line.geometry.setFromPoints([satPos, groundPos]);
    nadir[key].line.geometry.needsUpdate = true;
  }
  return { lat, lon, alt, velocity };
}

// ── Public: build orbit line ──────────────────────────────
export function buildOrbitLine3D(key, tleEntry, minutesAhead = 90) {
  if (!scene) return;
  if (orbitMeshes[key]) scene.remove(orbitMeshes[key]);
  const raw = groundTrackPoints(tleEntry.tle1, tleEntry.tle2, { minutesAhead, stepSec: 60 });
  const pts = raw.filter(Boolean).map((p) => llh2v3(p.lat, p.lon, p.alt));
  if (!pts.length) return;
  const line = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(pts),
    new THREE.LineBasicMaterial({ color: SAT_CONFIG[key]?.color||0xffffff, transparent:true, opacity:0.5 })
  );
  line.rotation.x = rotX;
  line.rotation.y = rotY;
  line.visible = showTracks;
  orbitMeshes[key] = line;
  scene.add(line);
}

// ── Public: focus ─────────────────────────────────────────
export function focusSat3D(key) {
  if (!markers[key]) return;
  const q   = new THREE.Quaternion().setFromEuler(new THREE.Euler(rotX, rotY, 0)).invert();
  const loc = markers[key].position.clone().applyQuaternion(q);
  rotY = -Math.atan2(loc.x, loc.z);
  rotX = -Math.asin(Math.max(-1, Math.min(1, loc.y / loc.length())));
}

// ── Public: display toggles ───────────────────────────────
export function set3DAutoRotate(v) { autoRotate = v; }
export function set3DShowTracks(v) {
  showTracks = v;
  Object.values(orbitMeshes).forEach((m) => { if (m) m.visible = v; });
}
export function set3DShowAtmo(v)   { if (atmosphere)  atmosphere.visible  = v; }
export function set3DShowStars(v)  { if (starsMesh)   starsMesh.visible   = v; }

// ── Mouse / touch interaction ─────────────────────────────
function setupInteraction(canvas, wrap) {
  canvas.addEventListener("mousedown",  (e) => { isDragging = true; prevMouse = { x:e.clientX, y:e.clientY }; });
  window.addEventListener("mouseup",    ()  => { isDragging = false; });
  window.addEventListener("mousemove",  (e) => {
    if (!isDragging) return;
    rotY += (e.clientX - prevMouse.x) * 0.005;
    rotX  = Math.max(-Math.PI/2, Math.min(Math.PI/2, rotX + (e.clientY-prevMouse.y)*0.005));
    prevMouse = { x:e.clientX, y:e.clientY };
  });
  canvas.addEventListener("wheel", (e) => {
    camera.position.z = Math.max(1.8, Math.min(6, camera.position.z + e.deltaY*0.005));
    e.preventDefault();
  }, { passive: false });

  let lt = null;
  canvas.addEventListener("touchstart", (e) => { if (e.touches.length===1) lt = { x:e.touches[0].clientX, y:e.touches[0].clientY }; });
  canvas.addEventListener("touchmove",  (e) => {
    if (e.touches.length===1 && lt) {
      rotY += (e.touches[0].clientX - lt.x) * 0.005;
      rotX  = Math.max(-Math.PI/2, Math.min(Math.PI/2, rotX + (e.touches[0].clientY-lt.y)*0.005));
      lt = { x:e.touches[0].clientX, y:e.touches[0].clientY };
    }
    e.preventDefault();
  }, { passive: false });
  canvas.addEventListener("touchend", () => { lt = null; });
}